/**
 * CR401: Deal Types Tab
 * CR318: Visible errors, auth-required state, fallback indicator
 * 
 * Registry table with metadata only - no ribbon controls.
 * 
 * Columns:
 * - deal_type_key (read-only)
 * - label
 * - sort_order
 * - is_enabled (Deal Type Enabled - NOT ribbon toggle)
 * - updated_at
 * 
 * Row actions:
 * - Edit Template → opens Template Editor
 * - Edit Ribbon → deep-links to Template Editor → Components → Ribbon
 */

import { useState, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { 
  Layers, 
  MoreHorizontal, 
  Edit, 
  Ribbon, 
  Save, 
  RefreshCw,
  Info,
  Lock,
  AlertCircle,
  Database,
  LogIn,
  AlertTriangle,
  Image,
} from "lucide-react";
import type { DealTypeRegistryEntry } from "@/providers/DealCardConfigProvider";

type DataSource = "edge" | "db-fallback" | null;

interface DealTypesTabProps {
  registry: DealTypeRegistryEntry[];
  loading: boolean;
  error?: string | null;
  dataSource?: DataSource;
  authRequired?: boolean;
  onEditTemplate: (dealTypeKey: string, variantKey: string) => void;
  onEditRibbon: (dealTypeKey: string) => void;
  onRefresh: () => void;
}

export function DealTypesTab({
  registry,
  loading,
  error,
  dataSource,
  authRequired,
  onEditTemplate,
  onEditRibbon,
  onRefresh,
}: DealTypesTabProps) {
  // Track edits per row
  const [edits, setEdits] = useState<Record<string, Partial<DealTypeRegistryEntry>>>({});
  const [saving, setSaving] = useState<string | null>(null);

  // Get current value (edit or original)
  const getValue = <K extends keyof DealTypeRegistryEntry>(
    key: string,
    field: K
  ): DealTypeRegistryEntry[K] => {
    const edit = edits[key]?.[field];
    if (edit !== undefined) return edit as DealTypeRegistryEntry[K];
    const original = registry.find(r => r.deal_type_key === key);
    return original?.[field] as DealTypeRegistryEntry[K];
  };

  // Handle edit
  const handleEdit = (key: string, field: keyof DealTypeRegistryEntry, value: unknown) => {
    setEdits(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));
  };

  // Check if row has unsaved changes
  const hasChanges = (key: string): boolean => {
    return Object.keys(edits[key] || {}).length > 0;
  };

  // Save row
  const saveRow = useCallback(async (dealTypeKey: string) => {
    const rowEdits = edits[dealTypeKey];
    if (!rowEdits || Object.keys(rowEdits).length === 0) return;

    setSaving(dealTypeKey);
    try {
      const { error } = await supabase.functions.invoke("command-center-deal-cards", {
        method: "POST",
        body: {
          _action: "update_registry",
          deal_type_key: dealTypeKey,
          ...rowEdits,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      toast.success(`Saved ${dealTypeKey}`);
      setEdits(prev => {
        const next = { ...prev };
        delete next[dealTypeKey];
        return next;
      });
      onRefresh();
    } catch (err) {
      console.error("[DealTypesTab] Save error:", err);
      const msg = err instanceof Error ? err.message : "Unknown error";
      toast.error(`Failed to save ${dealTypeKey}: ${msg}`);
    } finally {
      setSaving(null);
    }
  }, [edits, onRefresh]);

  // Auth required state
  if (authRequired) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center text-center space-y-4">
            <LogIn className="h-12 w-12 text-muted-foreground" />
            <div>
              <h3 className="text-lg font-semibold">Sign in required</h3>
              <p className="text-muted-foreground mt-1">
                Please sign in to view Deal Types configuration.
              </p>
            </div>
            <Button variant="outline" onClick={() => window.location.href = "/auth"}>
              Go to Sign In
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-96" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Error banner - CR317/CR318: Display error with context */}
      {error && (
        <Alert variant={dataSource === "db-fallback" ? "default" : "destructive"}>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {dataSource === "db-fallback" ? (
              <span><strong>Warning:</strong> {error}</span>
            ) : (
              <span><strong>Failed to load Deal Types:</strong> {error}</span>
            )}
          </AlertDescription>
        </Alert>
      )}
      
      {/* DB Fallback indicator (when successful fallback, no error shown) */}
      {dataSource === "db-fallback" && !error && (
        <Alert>
          <Database className="h-4 w-4" />
          <AlertDescription>
            <strong>Loaded from database fallback.</strong> Edge function was unavailable. 
            Template data may be limited.
          </AlertDescription>
        </Alert>
      )}
      
      {/* Unexpected empty state */}
      {!loading && registry.length === 0 && !error && !authRequired && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>
              <strong>Unexpected empty state:</strong> No deal types loaded. This may indicate a data issue.
            </span>
            <Button size="sm" variant="outline" onClick={onRefresh} className="ml-4">
              <RefreshCw className="h-3 w-3 mr-1" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}
      
      {/* Info banner */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          <strong>Deal Type Enabled</strong> controls whether this deal type is active in the system.
          Ribbon configuration has moved to the Template Editor → Components tab.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Deal Type Registry
            {dataSource === "db-fallback" && (
              <Badge variant="outline" className="ml-2 text-xs">
                <Database className="h-3 w-3 mr-1" />
                DB Fallback
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            Manage deal type metadata. For ribbon and visual configuration, use the Template Editor.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="max-h-[600px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[140px]">Deal Type Key</TableHead>
                  <TableHead>Label</TableHead>
                  <TableHead className="w-[120px]">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-1 cursor-help">
                            <Lock className="h-3 w-3 text-muted-foreground" />
                            <span>Ribbon Text</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[240px]">
                          <p className="text-xs">
                            Read-only. Edit in <strong>Deal Cards → Components → Ribbon</strong>.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableHead>
                  <TableHead className="w-[80px] text-center">Order</TableHead>
                  <TableHead className="w-[120px] text-center">
                    <div className="flex flex-col items-center">
                      <span>Deal Type</span>
                      <span className="text-[10px] font-normal text-muted-foreground">Enabled</span>
                    </div>
                  </TableHead>
                  <TableHead className="w-[120px] text-center">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex flex-col items-center cursor-help">
                            <span className="flex items-center gap-1"><Image className="h-3 w-3" />Flyer</span>
                            <span className="text-[10px] font-normal text-muted-foreground">Evidence</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[240px]">
                          <p className="text-xs">
                            When enabled, flyer page snippets appear in admin deal details for this deal type.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableHead>
                  <TableHead className="text-right">Last Updated</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {registry.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                      No deal types found
                    </TableCell>
                  </TableRow>
                ) : (
                  registry.map((entry) => {
                    const isSaving = saving === entry.deal_type_key;
                    const rowHasChanges = hasChanges(entry.deal_type_key);

                    return (
                      <TableRow key={entry.deal_type_key}>
                        <TableCell>
                          <Badge variant="outline" className="font-mono text-xs">
                            {entry.deal_type_key}
                          </Badge>
                        </TableCell>
                      <TableCell>
                          <Input
                            value={getValue(entry.deal_type_key, "label")}
                            onChange={(e) => handleEdit(entry.deal_type_key, "label", e.target.value)}
                            className="w-28 h-8 text-sm"
                          />
                        </TableCell>
                        {/* CR316: Ribbon Text is read-only - SSOT is Components → Ribbon */}
                        <TableCell>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-1">
                                  <Badge variant="outline" className="font-normal text-xs bg-muted/50 cursor-default">
                                    {entry.ribbon_text || entry.deal_type_key}
                                  </Badge>
                                  <Lock className="h-3 w-3 text-muted-foreground" />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-[240px]">
                                <p className="text-xs">
                                  Edit in <strong>Deal Cards → Components → Ribbon</strong>.
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                        <TableCell className="text-center">
                          <Input
                            type="number"
                            value={getValue(entry.deal_type_key, "sort_order")}
                            onChange={(e) => handleEdit(entry.deal_type_key, "sort_order", parseInt(e.target.value, 10))}
                            className="w-16 h-8 text-sm mx-auto"
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Switch
                            checked={getValue(entry.deal_type_key, "is_enabled")}
                            onCheckedChange={(v) => handleEdit(entry.deal_type_key, "is_enabled", v)}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Switch
                            checked={getValue(entry.deal_type_key, "show_flyer_evidence") ?? true}
                            onCheckedChange={(v) => handleEdit(entry.deal_type_key, "show_flyer_evidence", v)}
                          />
                        </TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground">
                          {entry.updated_at ? (
                            formatDistanceToNow(new Date(entry.updated_at), { addSuffix: true })
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {rowHasChanges && (
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => saveRow(entry.deal_type_key)}
                                disabled={isSaving}
                                className="h-7 px-2"
                              >
                                {isSaving ? (
                                  <RefreshCw className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Save className="h-3 w-3" />
                                )}
                              </Button>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => onEditTemplate(entry.deal_type_key, "standard_v2")}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit Template
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => onEditRibbon(entry.deal_type_key)}>
                                  <Ribbon className="h-4 w-4 mr-2" />
                                  Edit Ribbon
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
